import React from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import Box from 'components/atoms/Layout/Box/Box';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Typography, {H6} from 'components/atoms/Text/Typography/Typography';
import constants from 'helpers/constants';
import StyledTagGroupCard from 'components/organisms/Cards/TagGroupCard/TagGroupCard.styles';
import Chip from 'components/atoms/Chips/Chip/Chip';
import ChipList from 'components/atoms/Chips/ChipList/ChipList';
import {Dot} from 'assets/icons';

const TagGroupCard = React.forwardRef((props, ref) => {
  const {
    tagGroup,
    hasAutoTags,
    isActive,
    isUniverse,
    isLoading,
    variant,
    ...innerProps
  } = useComponentProps({
    variant: 'standard',
    ...props
  }, 'TagGroupCard');

  const compact = variant === 'compact';

  const renderDetails = () => {
    return <Box className="TagGroupCard-details">
      <Box className="TagGroupCard-detail">
        <Typography className="TagGroupCard-detail-label"
                    isLoading={isLoading}
                    variant="caption">Add points</Typography>
        <Typography className="TagGroupCard-detail-value"
                    isLoading={isLoading}
                    variant="caption">{tagGroup?.hasPoints ? 'Yes' : 'No'}</Typography>
      </Box>
      <Box className="TagGroupCard-detail">
        <Typography className="TagGroupCard-detail-label"
                    isLoading={isLoading}
                    variant="caption">Multiselect</Typography>
        <Typography className="TagGroupCard-detail-value"
                    isLoading={isLoading}
                    variant="caption">{tagGroup?.multiselect ? 'Yes' : 'No'}</Typography>
      </Box>
      <Box className="TagGroupCard-detail">
        <Typography className="TagGroupCard-detail-label"
                    isLoading={isLoading}
                    variant="caption">Autoselect</Typography>
        {(tagGroup?.autoTagType === constants.collection.autoTagTypes.byKeywords) ?
          <Chip className="TagGroupCard-detail-chip"
                tooltip={(!isActive || !hasAutoTags) ? (!isActive ? `${isUniverse ? 'Universe' : 'Collection'} is not active` : `Auto tags service is not active${isUniverse ? ' for deal flow' : ''}`) : ''}
                showTooltip={!isActive || !hasAutoTags}
                isLoading={isLoading}
                size="small" variant="filled"
                label="By keywords" color={(isActive && hasAutoTags) ? 'success' : 'warning'} /> : (
            (tagGroup?.autoTagType === constants.collection.autoTagTypes.byAI) ?
              <Chip className="TagGroupCard-detail-chip"
                    tooltip={(!isActive || !hasAutoTags) ? (!isActive ? `${isUniverse ? 'Universe' : 'Collection'} is not active` : `Auto tags service is not active${isUniverse ? ' for deal flow' : ''}`) : ''}
                    showTooltip={!isActive || !hasAutoTags}
                    isLoading={isLoading}
                    size="small" variant="filled"
                    label="By AI" color={(isActive && hasAutoTags) ? 'success' : 'warning'} /> : (
              (tagGroup?.autoTagType === constants.collection.autoTagTypes.byAIOnline) ?
                <Chip className="TagGroupCard-detail-chip"
                      tooltip={(!isActive || !hasAutoTags) ? (!isActive ? `${isUniverse ? 'Universe' : 'Collection'} is not active` : `Auto tags service is not active${isUniverse ? ' for deal flow' : ''}`) : ''}
                      showTooltip={!isActive || !hasAutoTags}
                      isLoading={isLoading}
                      size="small" variant="filled"
                      label="By AI (Online)" color={(isActive && hasAutoTags) ? 'success' : 'warning'} /> :
                <Chip className="TagGroupCard-detail-chip"
                      showTooltip={false}
                      isLoading={isLoading}
                      size="small" variant="filled"
                      label="Not active" />
              )
          )}
      </Box>
    </Box>
  }

  const renderTags = () => {
    const tags = !isLoading ? tagGroup?.tags : (new Array(Math.max(2, Math.ceil(Math.random() * 8))))
      .fill({value: utils.randomString(8, 16)});

    return <Box className="TagGroupCard-tags">
      <ChipList variant="compact"
                drawFromStart={true}>
        {tags?.map((t, idx) => {
          let description = t.description;
          if (!utils.isDefined(t.description)) {
            // see also actionCard
            if (tagGroup.autoTagType === constants.collection.autoTagTypes.byKeywords) {
              if (t.autoTagParams?.keywords?.length > 0) {
                description = t.autoTagParams.keywords.join(', ');
              }
            } else if ([constants.collection.autoTagTypes.byAI, constants.collection.autoTagTypes.byAIOnline].includes(tagGroup.autoTagType)) {
              if (t.autoTagParams?.description?.length > 0) {
                description = t.autoTagParams.description;
              }
            }

            if (tagGroup?.hasPoints) {
              description = `${description ? `${description} (` : ''}${t.points ?? 0} points${description ? `)` : ''}`;
            }
          }

          return <Chip key={idx}
                       size="small"
                       label={t.value}
                       tooltip={description}
                       TooltipProps={description ? {enterDelay: 0} : {}}
                       variant="outlined"
                       isLoading={isLoading}
                       icon={<Icon icon={Dot} size="smaller" color={tagGroup?.color || 'tagGroupDefault.dark'}/>}
                       color={tagGroup?.color || 'tagGroupDefault.dark'}/>
        })}
      </ChipList>
    </Box>
  }

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledTagGroupCard ref={ref} {...innerProps}>
    <Box className="TagGroupCard-content">
      <Box className="TagGroupCard-info">
        <H6 className="TagGroupCard-title"
            showTooltip={true}
            min={8} max={20}
            isLoading={isLoading}>{tagGroup?.name}</H6>
        {(compact && tagGroup?.multiselect) ? <Chip className="TagGroupCard-detail-chip"
                                                    isLoading={isLoading}
                                                    size="small"
                                                    label="Multiselect"
                                                    radius="round"
                                                    variant={tagGroup?.multiselect ? 'filled' : 'outlined'}
                                                    color={tagGroup?.multiselect ? 'primary' : 'secondary'} /> : null}
        {!compact ? renderDetails() : null}
      </Box>
    </Box>
    {renderTags()}
  </StyledTagGroupCard>
});

TagGroupCard.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  tagGroup: PropTypes.object,
  hasAutoTags: PropTypes.bool,
  isActive: PropTypes.bool,
  isUniverse: PropTypes.bool,
  isLoading: PropTypes.bool,
  variant: PropTypes.oneOfType([PropTypes.oneOf(['standard', 'compact']), PropTypes.string])
};

export default TagGroupCard;
